import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Select, Stack, Typography } from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'
import { Delete } from '@mui/icons-material'
import { useState } from 'react'
import { GLItemKind, GLItemStatus, GLItemStatuses } from '../../../DTO/GLItemDTO'
import { GLItemStatusHistoryDTO } from '../../../DTO/GLItemStatusHistoryDTO'

function statusEntries(kind: GLItemKind): Array<{ status?: GLItemStatus, name: string, hideInTabs?: boolean }> {
  return (GLItemStatuses[kind] || []) as Array<{ status?: GLItemStatus, name: string, hideInTabs?: boolean }>
}

function statusName(kind: GLItemKind, status: GLItemStatus): string {
  const entry = statusEntries(kind).find(s => s.status === status)
  return entry?.name || `${status}`
}

function toDateInput(raw?: string): string {
  if (!raw) {
    return ''
  }
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  const y = date.getFullYear()
  const m = `${date.getMonth() + 1}`.padStart(2, '0')
  const d = `${date.getDate()}`.padStart(2, '0')
  return `${y}-${m}-${d}`
}

export default function GLItemStatusHistoryAdmin(props: {
  glItemId: number
  kind: GLItemKind
}) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<GLItemStatusHistoryDTO[]>([])
  const [addStatus, setAddStatus] = useState<GLItemStatus | ''>('')
  const [addDate, setAddDate] = useState('')

  async function load() {
    setLoading(true)
    const list = await GLItemStatusHistoryDTO.listFromServer(props.glItemId)
    setRows(list)
    setLoading(false)
  }

  async function openDialog() {
    setOpen(true)
    await load()
  }

  async function changeDate(row: GLItemStatusHistoryDTO, value: string) {
    if (!value) {
      return
    }
    await row.saveDate(`${value}T12:00:00`)
    await load()
  }

  async function remove(row: GLItemStatusHistoryDTO) {
    await row.delete()
    await load()
  }

  async function addEntry() {
    if (addStatus === '') {
      return
    }
    await GLItemStatusHistoryDTO.upsert(
      props.glItemId,
      addStatus,
      addDate ? `${addDate}T12:00:00` : undefined,
    )
    setAddStatus('')
    setAddDate('')
    await load()
  }

  const used = rows.map(r => r.status)
  const addOptions = statusEntries(props.kind)
    .filter(s => s.status !== undefined && !s.hideInTabs)
    .filter(s => used.indexOf(s.status as GLItemStatus) < 0)

  return (
    <>
      <IconButton
        size="small"
        title="Statusverlauf"
        onClick={openDialog}
      >
        <HistoryIcon fontSize="small" />
      </IconButton>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Statusverlauf</DialogTitle>
        <DialogContent>
          {loading && <Box sx={{ color: '#666', paddingY: 1 }}>Lade…</Box>}
          {!loading && rows.length === 0 && (
            <Typography variant="body2" sx={{ color: '#666', marginBottom: 2 }}>
              Noch keine Statusdaten.
            </Typography>
          )}
          <Stack spacing={1} sx={{ marginBottom: 2 }}>
            {rows.map(row => (
              <Stack
                key={row.id}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ borderBottom: '1px solid #eee', paddingBottom: 1 }}
              >
                <Box sx={{ flexGrow: 1 }}>{statusName(props.kind, row.status)}</Box>
                <input
                  type="date"
                  value={toDateInput(row.date)}
                  onChange={e => changeDate(row, e.target.value)}
                />
                <IconButton size="small" title="Aus Historie entfernen" onClick={() => remove(row)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
          {addOptions.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Select
                size="small"
                displayEmpty
                value={addStatus === '' ? '' : String(addStatus)}
                onChange={e => {
                  const raw = e.target.value
                  setAddStatus(raw === '' ? '' : Number(raw))
                }}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">Status nachtragen</MenuItem>
                {addOptions.map(s => (
                  <MenuItem key={String(s.status)} value={String(s.status)}>{s.name}</MenuItem>
                ))}
              </Select>
              <input
                type="date"
                value={addDate}
                onChange={e => setAddDate(e.target.value)}
              />
              <Button size="small" onClick={addEntry} disabled={addStatus === ''}>
                Speichern
              </Button>
            </Stack>
          )}
          <Typography variant="caption" sx={{ display: 'block', marginTop: 2, color: '#666' }}>
            Jeder Status nur einmal. Erneutes Setzen überschreibt das Datum. Löschen ändert den aktuellen Status der Karte nicht.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
